import { calendarDisplayDate } from '../../../../utils/date-time';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { AgendaEstabelecimentoDate } from './List';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import ScheduleList from '../ScheduleList';
import { useAgendaContext } from '../ContextAgenda';
import { useAppContext } from '../../../../contexts/AppContext';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { Projecao } from './Projecao';
import { OffersDayPanel } from './OffersDayPanel';
import { useMediaQuery } from 'react-responsive';
import { EyeOff, Settings, ShoppingBag } from 'lucide-react';
import { Modal } from '../../../../components/Modal';
import { DateItemSettings } from './ItemSettings';

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const getRelativeDay = (date: string, timezone?: string) => {
    const diff = moment(date).startOf('day').diff(moment(calendarDisplayDate(new Date(), timezone)).startOf('day'), 'days');
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    if (diff === -1) return 'Ontem';
    return '';
};

interface DateItemProps {
    index: number;
    agenda: AgendaEstabelecimentoDate;
}

export const DateItem = ({ agenda, index }: DateItemProps) => {
    const { user } = useAuthContext();
    const { estabSession } = useEstabsContext();
    const { schedules } = useAgendaContext();
    const { hiddenValue } = useAppContext();
    const [floating, setFloating] = useState(false);
    const [openConfig, setOpenConfig] = useState(false);
    const [offsetWidth, setOffsetWidth] = useState(0);
    const [offsetLeft, setOffsetLeft] = useState(0);
    const headerRef = useRef<HTMLDivElement>(null);
    const isCompactViewport = useMediaQuery({ maxWidth: 1024 });
    const hasAgora = !!agenda?.agora;
    const hasProximo = !!agenda?.proximo;
    const saleOpportunitySummary = useMemo(() => {
        const clientIds = new Set<string>();
        let recurringCount = 0;
        let estimatedTotal = 0;

        (schedules || []).forEach((schedule) => {
            const opportunity = schedule?.saleOpportunity;
            if (!opportunity?.activeRecurringCount) {
                return;
            }

            const clientKey = String(schedule?.client?.uid || schedule?._id || '');
            if (!clientKey || clientIds.has(clientKey)) {
                return;
            }
            clientIds.add(clientKey);

            recurringCount += Number(opportunity.activeRecurringCount || 0);
            estimatedTotal += Number(opportunity.estimatedTotal || 0);
        });

        return {
            clientsCount: clientIds.size,
            recurringCount,
            estimatedTotal,
        };
    }, [schedules]);
    const hasSaleOpportunities = saleOpportunitySummary.clientsCount > 0;
    const saleOpportunityValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(Number(saleOpportunitySummary.estimatedTotal || 0));
    const weekday = capitalize(moment(agenda.date).format('dddd'));
    const relativeDay = getRelativeDay(agenda.date, estabSession?.timezone);
    const dayLabel = moment(agenda.date).format('D [de] MMMM');
    const countLabel = !agenda.total
        ? 'Sem agendamentos'
        : `${agenda.total} ${agenda.total === 1 ? 'agendamento' : 'agendamentos'}`;
    // Barra compacta: o mais util ao rolar e o que esta acontecendo ou vem a seguir.
    const floatingStatus = hasAgora
        ? { label: 'Agora', ...agenda.agora }
        : hasProximo
          ? { label: 'Próximo', ...agenda.proximo }
          : null;

    useEffect(() => {
        const handleScroll = () => {
            if (headerRef.current) {
                // A barra entra quando o cabecalho do dia some sob o topo da tela.
                const barTop = isCompactViewport ? 68 : 0;
                setFloating(headerRef.current.getBoundingClientRect().bottom <= barTop);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isCompactViewport]);

    useEffect(() => {
        const element = headerRef.current;
        if (!element) {
            return;
        }

        const updateOffsets = () => {
            const rect = element.getBoundingClientRect();
            setOffsetWidth(rect.width);
            setOffsetLeft(rect.left);
        };

        updateOffsets();

        const resizeObserver = new ResizeObserver(() => updateOffsets());
        resizeObserver.observe(element);

        window.addEventListener('resize', updateOffsets);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateOffsets);
        };
    }, []);

    return (
        <div className="mb-3">
            <div ref={headerRef} className="py-2 text-base-content">
                <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-2xl font-bold leading-tight tracking-[-0.025em]">{relativeDay || weekday}</h2>
                        <p className="mt-0.5 text-sm text-base-content/60">
                            {relativeDay ? `${weekday}, ${dayLabel}` : capitalize(dayLabel)} · {countLabel}
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <OffersDayPanel date={agenda.date} />
                        <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[rgb(118_118_128/0.12)] text-base-content/70 transition-colors hover:bg-[rgb(118_118_128/0.2)] hover:text-base-content"
                            onClick={() => setOpenConfig(true)}
                            title="Configurações do dia"
                            aria-label="Configurações do dia"
                        >
                            <Settings className="text-sm" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                {(hasAgora || hasProximo || hasSaleOpportunities) && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
                        {hasAgora && (
                            <span className="flex min-w-0 max-w-full items-center gap-1.5">
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                                <strong className="shrink-0 font-semibold text-primary">Agora {agenda.agora.horario}</strong>
                                <span className="truncate text-base-content/70">{agenda.agora.cliente || agenda.agora.servico}</span>
                            </span>
                        )}
                        {hasProximo && (
                            <span className="flex min-w-0 max-w-full items-center gap-1.5">
                                <strong className="shrink-0 font-semibold">Próximo {agenda.proximo.horario}</strong>
                                <span className="truncate text-base-content/70">{agenda.proximo.cliente || agenda.proximo.servico}</span>
                            </span>
                        )}
                        {hasSaleOpportunities && (
                            <span
                                className="sf-tag gap-1.5"
                                data-tone="green"
                                title={`${saleOpportunitySummary.clientsCount} clientes · ${saleOpportunitySummary.recurringCount} contratos`}
                            >
                                <ShoppingBag className="text-[10px]" aria-hidden="true" />
                                Potencial de venda
                                {hiddenValue ? <EyeOff aria-label="Valor oculto" /> : ` ${saleOpportunityValue}`}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {floating && (
                <div
                    className="sf-day-bar fixed z-30 text-base-content"
                    style={{
                        top: 'var(--sf-content-header-top, 0px)',
                        width: offsetWidth ? `${offsetWidth}px` : undefined,
                        left: `${offsetLeft}px`,
                    }}
                >
                    <span className="shrink-0 text-sm font-semibold">
                        {relativeDay || weekday}
                        <span className="font-normal text-base-content/60"> · {moment(agenda.date).format('D [de] MMM')}</span>
                    </span>
                    <span className="ml-auto flex min-w-0 items-center gap-1.5 text-sm">
                        {floatingStatus ? (
                            <>
                                {hasAgora && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />}
                                <strong className={`shrink-0 font-semibold ${hasAgora ? 'text-primary' : ''}`}>
                                    {floatingStatus.label} {floatingStatus.horario}
                                </strong>
                                <span className="truncate text-base-content/70">{floatingStatus.cliente || floatingStatus.servico}</span>
                            </>
                        ) : (
                            <span className="truncate text-base-content/60">{countLabel}</span>
                        )}
                    </span>
                </div>
            )}

            {
                (user?.root || user?.admin) &&
                <div
                    className="sf-projection-bar fixed bottom-0 z-30 text-base-content"
                    style={{
                        width: offsetWidth ? `${offsetWidth}px` : undefined,
                        left: `${offsetLeft}px`,
                    }}
                >
                    <Projecao />
                </div>
            }

            <ScheduleList agenda={agenda} />

            {openConfig && (
                <Modal
                    title="Configurações do dia"
                    subTitle={capitalize(moment(agenda.date).format('dddd, D [de] MMMM [de] YYYY'))}
                    onClose={() => setOpenConfig(false)}
                    desktopClassName="sm:!max-w-lg"
                >
                    <DateItemSettings date={agenda.date} onClose={() => setOpenConfig(false)} />
                </Modal>
            )}
        </div>
    );
};
